import {
  Component,
  OnInit,
  signal,
  inject,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  AfterViewChecked,
  computed,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../../../../core/models/user.model';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { TaskPriority, TaskStatus } from '../../../../core/models/crm-types';
import { BranchContextService } from '../../../../core/services/branch-context.service';
import { LeadService } from '../../../leads/services/lead.service';
import { UserService } from '../../../settings/services/user.service';
import { TaskService } from '../../services/task.service';
import { UploadedFile } from '../../../../core/models/uploadedFile.model';
import { Comment, Task, Activity } from '../../models/tasks.model';
import { AuthService } from '../../../../core/services/auth.service';
import { OpportunityService } from '../../../opportunities/services/opportunity.service';
import { ClientService } from '../../../clients/services/client.service';
import Quill from 'quill';
import QuillTableBetter from 'quill-table-better';
import 'quill/dist/quill.snow.css';
import 'quill-table-better/dist/quill-table-better.css'
import { Branch } from '../../../settings/models/branch.model';
import { UserRoleType } from '../../../../core/models/crm-enums';
import { BranchService } from '../../../settings/services/branch.service';

interface RelatedEntity {
  id: string;
  name: string;
  type: 'lead' | 'client' | 'opportunity';
}

@Component({
  selector: 'app-task-modal-upsert',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './task-modal-upsert.component.html',
  styleUrls: ['./task-modal-upsert.component.scss'],
})
export class TaskModalUpsertComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() taskId?: string | null;
  @Input() preselectedLeadId?: string | null;
  @Input() preselectedClientId?: string | null;
  @Input() preselectedOpportunityId?: string | null;
  @Output() close = new EventEmitter<void>();
  @ViewChild('editor', { static: false }) editorRef!: ElementRef;
  @ViewChild('newCommentEditor', { static: false }) newCommentEditorRef!: ElementRef;

  isRecurring = false;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' = 'WEEKLY';
  startDate = '';
  daysInAdvance = 1;
  endDate = '';
  recurringTaskId: string | null = null;

  frequencyOptions = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'YEARLY', label: 'Yearly' }
  ];

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private taskService = inject(TaskService);
  private userService = inject(UserService);
  private leadService = inject(LeadService);
  private opportunityService = inject(OpportunityService);
  private clientService = inject(ClientService);
  // private branchContext = inject(BranchContextService);
  private authService = inject(AuthService);
  // private route = inject(ActivatedRoute);
  private branchService = inject(BranchService);

  private initialFormValue: any = null;

  task: Task | null = null;
  taskForm: FormGroup;
  isLoading = signal(false);
  private quill!: Quill;
  private isUpdatingFromQuill = false;

  // Dropdown state
  showAssigneeDropdown = false;
  assignSearchQuery = '';
  users: User[] = [];
  selectedUser: User | null = null;

  // Related entity dropdown state
  showRelatedDropdown = false;
  relatedSearchQuery = '';
  relatedEntityType: 'lead' | 'client' | 'opportunity' = 'lead';
  selectedRelatedEntity: RelatedEntity | null = null;
  relatedEntities: RelatedEntity[] = [];
  isSearchingRelated = false;
  private searchSubject = new Subject<string>();

  // File handling
  uploadedFiles: UploadedFile[] = [];
  deletedDocuments: string[] = [];

  // Comments
  comments: Comment[] = [];
  newCommentText = '';
  newCommentFiles: UploadedFile[] = [];
  editingCommentId: string | null = null;
  deletedCommentDocuments: string[] = [];

  // Activities
  activities: Activity[] = [];

  // Current user
  currentUser: User | null = null;

  priorityOptions: TaskPriority[] = ['HIGH', 'MEDIUM', 'LOW'];
  statusOptions: TaskStatus[] = [
    'OPEN',
    'IN_PROGRESS',
    'COMPLETED',
    'ON_HOLD',
    'CANCELLED',
  ];

  private newCommentQuill!: Quill;
  private commentEditors: Map<string, Quill> = new Map();

  showBranchField = true;
  // availableBranches = computed(() => this.branchContext.availableBranches());
  availableBranches = signal<Branch[]>([]);

  private initialWatchers = signal<User[]>([]);
  private addedWatchers = signal<Set<string>>(new Set());
  private removedWatchers = signal<Set<string>>(new Set());

  selectedWatcherToAdd: string = '';

  allUsers = signal<User[]>([]);

  // Current watchers (initial + added - removed)
  currentWatchers = computed(() => {
    const initial = this.initialWatchers();
    const added = this.addedWatchers();
    const removed = this.removedWatchers();

    // Start with initial watchers
    let watchers = initial.filter(w => !removed.has(w.id));

    // Add new watchers
    const newWatchers = this.allUsers().filter(u => added.has(u.id));
    watchers = [...watchers, ...newWatchers];

    return watchers;
  });

  // Available options for the dropdown (exclude assigned user and current watchers)
  availableWatcherOptions = computed(() => {
    const assignedUserId = this.taskForm?.get('assigneeId')?.value;
    const currentWatcherIds = new Set(this.currentWatchers().map(w => w.id));

    return this.allUsers()
      .filter(user => user.id !== assignedUserId && !currentWatcherIds.has(user.id))
      .map(user => ({
        value: user.id,
        label: user.username
      }));
  });

  // Computed property for hasChanges
  // Update the hasChanges getter
  get hasChanges(): boolean {
    if (!this.initialFormValue) {
      return false;
    }

    // Check if form values have changed
    const formChanged = this.hasFormChanged();

    // Check if watchers have changed
    const watchersChanged = this.hasWatchersChanged();

    // Check if recurring task settings have changed
    const recurringChanged = this.hasRecurringChanged();

    return formChanged || watchersChanged || recurringChanged;
  }

  // Add new method to check recurring changes
  private hasRecurringChanged(): boolean {
    // If task doesn't exist yet, check if recurring is enabled
    if (!this.task) {
      return this.isRecurring;
    }

    // Compare current recurring settings with initial task state
    const wasRecurring = !!this.task.recurringTask;

    // If recurring status changed
    if (this.isRecurring !== wasRecurring) {
      return true;
    }

    // If currently recurring, check if any settings changed
    if (this.isRecurring && this.task.recurringTask) {
      const initialStartDate = this.task.recurringTask.startDate?.split('T')[0] || '';
      const initialEndDate = this.task.recurringTask.endDate?.split('T')[0] || '';

      return (
        this.frequency !== this.task.recurringTask.frequency ||
        this.startDate !== initialStartDate ||
        this.daysInAdvance !== this.task.recurringTask.daysInAdvance ||
        this.endDate !== initialEndDate
      );
    }

    return false;
  }

  private hasFormChanged(): boolean {
    const currentValue = this.taskForm.value;

    // Compare each form field
    return Object.keys(currentValue).some(key => {
      const current = currentValue[key];
      const initial = this.initialFormValue[key];

      // Handle arrays
      if (Array.isArray(current) && Array.isArray(initial)) {
        return JSON.stringify(current.sort()) !== JSON.stringify(initial.sort());
      }

      // Handle null/undefined/empty string equivalence
      const currentNormalized = current === null || current === undefined || current === '' ? null : current;
      const initialNormalized = initial === null || initial === undefined || initial === '' ? null : initial;

      return currentNormalized !== initialNormalized;
    });
  }

  hasWatchersChanged(): boolean {
    // If there are any added or removed watchers, there are changes
    return this.addedWatchers().size > 0 || this.removedWatchers().size > 0;
  }

  constructor() {
    this.currentUser = this.authService.currentUser();
    // Calculate default due date (7 days from now)
    const defaultDueDate = new Date();
    // defaultDueDate.setDate(defaultDueDate.getDate() + 7);
    const defaultDueDateString = defaultDueDate.toISOString().split('T')[0];

    this.taskForm = this.fb.group({
      name: ['', Validators.required],
      assigneeId: ['', Validators.required],
      description: [''],
      dueDate: [defaultDueDateString, Validators.required],
      priority: ['MEDIUM', Validators.required],
      status: ['OPEN', Validators.required],
      leadId: [null],
      branchId: ['', Validators.required],
      clientId: [null],
      opportunityId: [null],
    });
  }

  async ngOnInit() {
    this.currentUser = this.authService.currentUser();

    this.loadBranchesAndData();
    this.setupSearchObservable();

    // Initialize empty entities array
    this.relatedEntities = [];

    // Set preselected entity if provided
    if (this.preselectedLeadId) {
      this.loadPreselectedLead();
    } else if (this.preselectedClientId) {
      this.loadPreselectedClient();
    } else if (this.preselectedOpportunityId) {
      this.loadPreselectedOpportunity();
    }

    if (!this.taskId) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.startDate = tomorrow.toISOString().split('T')[0];
    }
  }

  private loadBranchesAndData(): void {
    const user = this.currentUser;

    if (!user) {
      console.warn('No user found, cannot load branches');
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);

    // Determine which branches observable to use
    const branches$ = this.authService.hasRole([UserRoleType.ADMIN, UserRoleType.OPERATIONS, UserRoleType.RESEARCH])
      ? this.branchService.getBranches(1, 100, { isActive: true }).pipe(
        map(response => response.results)
      )
      : of(user.assignedBranches || []);

    const users$ = this.userService.getUsers(1, 100, { status: 'ACTIVE' });

    // Load both branches and users together
    forkJoin({
      branches: branches$,
      users: users$
    }).subscribe({
      next: ({ branches, users }) => {
        // Set branches
        this.availableBranches.set(branches);
        console.log('Loaded branches:', branches.length);

        // Set users
        this.users = users.results;
        this.allUsers.set(this.users);

        // Now handle task loading or defaults
        if (this.taskId) {
          // It's an existing task, load its data
          this.loadTask();
        } else {
          // It's a new task, set defaults
          this.setDefaults();

          // Store initial form value
          setTimeout(() => {
            this.initialFormValue = { ...this.taskForm.value };
          }, 0);

          this.isLoading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.availableBranches.set([]);
        this.isLoading.set(false);
      }
    });
  }

  // New method to set defaults
  private setDefaults(): void {
    console.log('Setting defaults...', {
      currentUser: this.currentUser?.id,
      branches: this.availableBranches().length
    });

    // Set current user as assignee
    if (this.currentUser) {
      this.taskForm.patchValue({ assigneeId: this.currentUser.id });
      this.selectedUser = this.users.find(u => u.id === this.currentUser!.id) || null;
      console.log('Set assigneeId to:', this.currentUser.id);
    } else {
      console.warn('No current user found when setting defaults!');
    }

    // Set default branch
    const branches = this.availableBranches();
    if (branches.length === 1) {
      this.taskForm.patchValue({ branchId: branches[0].id });
      console.log('Set branchId to single available branch:', branches[0].id);
    }
  }

  private async loadBranches(): Promise<void> {
    console.log('Loading branches for task modal component');
    const user = this.currentUser;

    if (!user) {
      console.warn('No user found, cannot load branches');
      return;
    }

    // Load branches based on user role
    if (this.authService.hasRole([UserRoleType.ADMIN, UserRoleType.OPERATIONS, UserRoleType.RESEARCH])) {
      return new Promise((resolve) => {
        this.branchService.getBranches(1, 100, { isActive: true }).subscribe({
          next: (response) => {
            this.availableBranches.set(response.results);
            console.log('Loaded branches in component:', response.results.length);
            resolve();
          },
          error: (error) => {
            console.error('Error loading branches:', error);
            this.availableBranches.set([]);
            resolve(); // Resolve anyway to not block
          }
        });
      });
    } else {
      // Regular user - use assigned branches
      this.availableBranches.set([...(user.assignedBranches || [])]);
    }
  }

  ngAfterViewInit() {
    Quill.register({
      'modules/table-better': QuillTableBetter
    }, true);

    // Main description editor
    this.quill = new Quill(this.editorRef.nativeElement, {
      theme: 'snow',
      modules: {
        table: false,
        toolbar: [
          ['bold', 'italic', 'underline'],
          ['link'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }], // Add alignment
          ['clean'],
        ],
        'table-better': {
          language: 'en_US',
          menus: ['column', 'row'],
          toolbarTable: false
        },
        keyboard: {
          bindings: QuillTableBetter.keyboardBindings
        },
        clipboard: {
          matchVisual: false
        }
      },
    });

    // Add copy handler for main editor
    this.quill.root.addEventListener('copy', (e: ClipboardEvent) => {
      if (e.clipboardData) {
        e.preventDefault();
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const fragment = range.cloneContents();
        const div = document.createElement('div');
        div.appendChild(fragment);

        const html = this.addInlineStylesToTables(div.innerHTML);
        const text = div.textContent || '';

        e.clipboardData.setData('text/html', html);
        e.clipboardData.setData('text/plain', text);
      }
    });

    this.quill.on('text-change', () => {
      if (!this.isUpdatingFromQuill) {
        const content = this.quill.root.innerHTML;
        this.isUpdatingFromQuill = true;
        this.taskForm.patchValue({ description: content });
        this.isUpdatingFromQuill = false;
      }
    });

    this.initializeNewCommentEditor();

    this.taskForm.get('assigneeId')?.valueChanges.subscribe(assignedUserId => {
      const currentWatchers = this.currentWatchers();
      const assignedWatcher = currentWatchers.find(w => w.id === assignedUserId);
      if (assignedWatcher) {
        this.removeWatcher(assignedUserId);
      }
    });
  }

  ngAfterViewChecked() {
    // Initialize readonly editors for any comments that don't have them yet
    this.comments.forEach(comment => {
      if (!comment.isEditing && !this.commentEditors.has(`readonly-${comment.id}`)) {
        this.initializeReadonlyCommentEditor(comment);
      }
    });
  }

  private addInlineStylesToTables(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;

    // Preserve alignment in paragraphs
    const paragraphs = div.querySelectorAll('p');
    paragraphs.forEach(p => {
      const currentStyle = p.getAttribute('style') || '';
      if (p.classList.contains('ql-align-center')) {
        p.setAttribute('style', currentStyle + ' text-align: center;');
      } else if (p.classList.contains('ql-align-right')) {
        p.setAttribute('style', currentStyle + ' text-align: right;');
      } else if (p.classList.contains('ql-align-justify')) {
        p.setAttribute('style', currentStyle + ' text-align: justify;');
      }
    });

    // Add inline styles to tables
    const tables = div.querySelectorAll('table');
    tables.forEach(table => {
      table.setAttribute('style', 'border-collapse: collapse; width: 100%; margin: 0; border: 1px solid #000;');

      const cells = table.querySelectorAll('td, th');
      cells.forEach(cell => {
        const isHeader = cell.tagName === 'TH';
        const styles = [
          'border: 1px solid #000',
          'padding: 4px',
          'vertical-align: top',
        ];

        // Preserve text alignment in cells
        if (cell.classList.contains('ql-align-center')) {
          styles.push('text-align: center');
        } else if (cell.classList.contains('ql-align-right')) {
          styles.push('text-align: right');
        } else if (cell.classList.contains('ql-align-justify')) {
          styles.push('text-align: justify');
        } else {
          styles.push('text-align: left');
        }

        if (isHeader) {
          styles.push('background-color: #fff', 'font-weight: 600');
        }

        cell.setAttribute('style', styles.join('; ') + ';');
      });

      // Add alternating row colors
      const rows = table.querySelectorAll('tr');
      rows.forEach((row, index) => {
        if (index % 2 === 1 && row.querySelector('td')) {
          const cells = row.querySelectorAll('td');
          cells.forEach(cell => {
            const currentStyle = cell.getAttribute('style') || '';
            cell.setAttribute('style', currentStyle + ' background-color: #fff;');
          });
        }
      });
    });

    return div.innerHTML;
  }

  ngOnDestroy() {
    // Clean up Quill instance
    if (this.quill) {
      this.quill = null as any;
    }
  }

  setupSearchObservable() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          this.isSearchingRelated = true;
          return this.searchRelatedEntities(query);
        })
      )
      .subscribe({
        next: (results) => {
          this.relatedEntities = results;
          this.isSearchingRelated = false;
        },
        error: (error) => {
          console.error('Search error:', error);
          this.isSearchingRelated = false;
        },
      });
  }

  searchRelatedEntities(query: string): Observable<RelatedEntity[]> {
    switch (this.relatedEntityType) {
      case 'lead':
        return this.leadService
          .getLeads({ page: 1, limit: 20, search: query })
          .pipe(
            map((response) =>
              response.results.map((lead) => ({
                id: lead.id,
                name: lead.name,
                type: 'lead' as const,
              }))
            )
          );
      case 'client':
        return this.clientService
          .getClients({ page: 1, limit: 20, search: query })
          .pipe(
            map((response) =>
              response.results.map((lead) => ({
                id: lead.id,
                name: lead.name,
                type: 'client' as const,
              }))
            )
          );
      case 'opportunity':
        return this.opportunityService
          .getOpportunities({ page: 1, limit: 20, search: query })
          .pipe(
            map((response) =>
              response.results.map((opp) => ({
                id: opp.id,
                name: opp.name,
                type: 'opportunity' as const,
              }))
            )
          );
    }
  }

  loadPreselectedLead() {
    if (!this.preselectedLeadId) return;

    this.leadService.getLeadById(this.preselectedLeadId).subscribe({
      next: (lead) => {
        this.selectedRelatedEntity = {
          id: lead.id,
          name: lead.name,
          type: 'lead',
        };
        this.relatedEntityType = 'lead';
        this.taskForm.patchValue({ leadId: lead.id });
      },
    });
  }

  loadPreselectedClient() {
    if (!this.preselectedClientId) return;

    // Use lead service as placeholder for now
    this.clientService.getClientById(this.preselectedClientId).subscribe({
      next: (client) => {
        this.selectedRelatedEntity = {
          id: client.id,
          name: client.name,
          type: 'client',
        };
        this.relatedEntityType = 'client';
        this.taskForm.patchValue({ clientId: client.id });
      },
    });
  }

  loadPreselectedOpportunity() {
    if (!this.preselectedOpportunityId) return;

    this.opportunityService
      .getOpportunityById(this.preselectedOpportunityId)
      .subscribe({
        next: (opp) => {
          this.selectedRelatedEntity = {
            id: opp.id,
            name: opp.name,
            type: 'opportunity',
          };
          this.relatedEntityType = 'opportunity';
          this.taskForm.patchValue({ opportunityId: opp.id });
        },
      });
  }

  loadInitialData(): void {
    this.isLoading.set(true);
    this.userService.getUsers(1, 100, { status: 'ACTIVE' }).subscribe({
      next: (response) => {
        this.users = response.results;
        this.allUsers.set(this.users);
        if (this.taskId) {
          // It's an existing task, load its data
          this.loadTask();
        } else {
          // It's a new task, set the current user as the default assignee
          if (this.currentUser) {
            this.taskForm.patchValue({ assigneeId: this.currentUser.id });
            this.selectedUser = this.users.find(u => u.id === this.currentUser!.id) || null;
          }
          const branches = this.availableBranches();
          if (branches.length === 1) {
            this.taskForm.patchValue({ branchId: branches[0].id });
          }

          // Store initial form value
          setTimeout(() => {
            this.initialFormValue = { ...this.taskForm.value };
          }, 0);
          this.isLoading.set(false);
        }
      },
      error: () => this.isLoading.set(false),
    });
  }

  loadTask() {
    this.isLoading.set(true);
    this.taskService.getTaskById(this.taskId!).subscribe({
      next: (task) => {
        this.task = task;
        this.taskForm.patchValue({
          name: task.name,
          assigneeId: task.assigneeId,
          description: task.description,
          dueDate: task.dueDate
            ? new Date(task.dueDate).toISOString().split('T')[0]
            : '',
          priority: task.priority,
          status: task.status,
          leadId: task.leadId,
          clientId: task.clientId,
          opportunityId: task.opportunityId,
          branchId: task.branchId || ''
        });

        if (task.watchers) {
          this.initialWatchers.set(task.watchers);
        }

        const initialDescription =
          this.taskForm.get('description')?.value || '';
        if (initialDescription) {
          this.quill.root.innerHTML = initialDescription;
        }

        if (this.currentUser?.id != this.task.creatorId) {
          this.taskForm.get('description')?.disable();
          this.quill.enable(false);
        }
        // Set selected user
        this.selectedUser =
          this.users.find((u) => u.id === task.assigneeId) || null;

        // Set selected related entity
        if (task.leadId && task.lead) {
          this.selectedRelatedEntity = {
            id: task.leadId,
            name: task.lead.name,
            type: 'lead',
          };
          this.relatedEntityType = 'lead';
        } else if (task.clientId && task.client) {
          this.selectedRelatedEntity = {
            id: task.clientId,
            name: task.client.name,
            type: 'client',
          };
          this.relatedEntityType = 'client';
        } else if (task.opportunityId && task.opportunity) {
          this.selectedRelatedEntity = {
            id: task.opportunityId,
            name: task.opportunity.name,
            type: 'opportunity',
          };
          this.relatedEntityType = 'opportunity';
        }

        // Load existing files
        if (task.documents) {
          this.uploadedFiles = task.documents.map((a) => ({
            id: a.id,
            fileName: a.fileName,
            fileType: a.fileType,
            fileSize: a.fileSize,
            isExisting: true,
            fileUrl: a.fileUrl,
          }));
        }

        // Load activities
        if (task.activities) {
          this.activities = task.activities.sort(
            (a, b) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        }

        this.loadComments();

        const taskBranchInAvailable = this.availableBranches().some(b => b.id === task.branchId);
        if (!taskBranchInAvailable) {
          this.showBranchField = false;
          this.taskForm.get('branchId')?.clearValidators();
          this.taskForm.get('branchId')?.updateValueAndValidity();
        }

        if (task.recurringTask) {
          this.isRecurring = true;
          this.frequency = task.recurringTask.frequency;
          this.startDate = task.recurringTask.startDate?.split('T')[0] || '';
          this.daysInAdvance = task.recurringTask.daysInAdvance || 1;
          this.endDate = task.recurringTask.endDate?.split('T')[0] || '';
          this.recurringTaskId = task.recurringTask.id;
        }

        setTimeout(() => {
          this.initialFormValue = { ...this.taskForm.value };
        }, 0);

        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  addWatcher(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const userId = select.value;

    if (!userId) return;

    // Check if this was an initial watcher that was removed
    const wasInitialWatcher = this.initialWatchers().some(w => w.id === userId);

    if (wasInitialWatcher) {
      // Remove from removed set
      this.removedWatchers.update(set => {
        const newSet = new Set(set);
        newSet.delete(userId);
        return newSet;
      });
    } else {
      // Add to added set
      this.addedWatchers.update(set => {
        const newSet = new Set(set);
        newSet.add(userId);
        return newSet;
      });
    }

    // Reset the select
    this.selectedWatcherToAdd = '';
  }

  removeWatcher(userId: string): void {
    const wasInitialWatcher = this.initialWatchers().some(w => w.id === userId);

    if (wasInitialWatcher) {
      // Add to removed set
      this.removedWatchers.update(set => {
        const newSet = new Set(set);
        newSet.add(userId);
        return newSet;
      });
    } else {
      // Remove from added set
      this.addedWatchers.update(set => {
        const newSet = new Set(set);
        newSet.delete(userId);
        return newSet;
      });
    }
  }

  private getWatcherChanges(): { addWatcherIds: string; removeWatcherIds: string } {
    const addWatcherIds = Array.from(this.addedWatchers()).join(',');
    const removeWatcherIds = Array.from(this.removedWatchers()).join(',');

    return { addWatcherIds, removeWatcherIds };
  }

  trackByWatcherId(index: number, watcher: User): string {
    return watcher.id;
  }

  // Related entity dropdown methods
  toggleRelatedDropdown(event: Event) {
    event.stopPropagation();
    this.showRelatedDropdown = !this.showRelatedDropdown;
    if (this.showRelatedDropdown) {
      this.relatedSearchQuery = '';
      this.searchSubject.next('');
    }
  }

  changeRelatedType(type: 'lead' | 'client' | 'opportunity', event: Event) {
    event.stopPropagation();
    this.relatedEntityType = type;
    this.relatedSearchQuery = '';
    this.relatedEntities = []; // Clear previous results
    this.loadInitialEntities();
  }

  loadInitialEntities() {
    this.isSearchingRelated = true;
    this.searchRelatedEntities('').subscribe({
      next: (results) => {
        this.relatedEntities = results;
        this.isSearchingRelated = false;
      },
      error: (error) => {
        console.error('Load error:', error);
        this.isSearchingRelated = false;
      },
    });
  }

  onRelatedSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.relatedSearchQuery = value;
    this.searchSubject.next(value);
  }

  selectRelatedEntity(entity: RelatedEntity, event: Event) {
    event.stopPropagation();
    this.selectedRelatedEntity = entity;

    // Clear all related fields first
    this.taskForm.patchValue({
      leadId: null,
      clientId: null,
      opportunityId: null,
    });

    // Set the selected entity
    switch (entity.type) {
      case 'lead':
        this.taskForm.patchValue({ leadId: entity.id });
        break;
      case 'client':
        this.taskForm.patchValue({ clientId: entity.id });
        break;
      case 'opportunity':
        this.taskForm.patchValue({ opportunityId: entity.id });
        break;
    }

    this.showRelatedDropdown = false;
    this.relatedSearchQuery = '';
  }

  removeRelatedEntity() {
    this.selectedRelatedEntity = null;
    this.taskForm.patchValue({
      leadId: null,
      clientId: null,
      opportunityId: null,
    });
  }

  getRelatedEntityTypeLabel(): string {
    switch (this.relatedEntityType) {
      case 'lead':
        return 'Lead';
      case 'client':
        return 'Client';
      case 'opportunity':
        return 'Opportunity';
    }
  }

  getRelatedEntityIcon(): string {
    switch (this.relatedEntityType) {
      case 'lead':
        return 'user';
      case 'client':
        return 'building';
      case 'opportunity':
        return 'trophy';
    }
  }

  closeModal() {
    this.close.emit();
  }

  maximizeModal() {
    if (this.taskId) {
      this.router.navigateByUrl('/tasks/' + this.taskId);
    } else {
      this.router.navigate(['/tasks/new']);
    }
  }

  saveTask() {
    if (this.taskForm.invalid) {
      this.taskForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const formData = new FormData();

    // Add form fields
    Object.keys(this.taskForm.value).forEach((key) => {
      const value = this.taskForm.value[key];
      if (value !== null && value !== undefined) {
        if (key === 'dueDate') {
          formData.append(key, new Date(value).toISOString());
        } else {
          formData.append(key, value);
        }
      }
    });

    // Add recurring task data for creation
    if (this.isRecurring && !this.taskId) {
      formData.append('isRecurring', 'true');
      formData.append('frequency', this.frequency);
      formData.append('startDate', new Date(this.startDate).toISOString());
      formData.append('daysInAdvance', this.daysInAdvance.toString());
      if (this.endDate) {
        formData.append('endDate', new Date(this.endDate).toISOString());
      }
    }

    // Add new files
    this.uploadedFiles.forEach((file) => {
      if (file.file && !file.isExisting) {
        formData.append('attachments', file.file);
      }
    });

    // Add deleted documents
    if (this.taskId) {
      formData.append('deletedDocuments', JSON.stringify(this.deletedDocuments));
    }

    // Add watchers
    const watcherChanges = this.getWatcherChanges();
    if (this.taskId) {
      formData.append('addWatcherIds', watcherChanges.addWatcherIds);
      formData.append('removeWatcherIds', watcherChanges.removeWatcherIds);
    } else {
      const allWatcherIds = this.currentWatchers().map(w => w.id).join(',');
      if (allWatcherIds) {
        formData.append('addWatcherIds', allWatcherIds);
      }
    }

    const saveOperation$ = this.taskId
      ? this.updateTaskWithRecurring(formData)
      : this.taskService.createTask(formData);

    saveOperation$.subscribe({
      next: () => {
        this.isLoading.set(false);
        this.closeModal();
      },
      error: () => this.isLoading.set(false)
    });
  }

  private updateTaskWithRecurring(formData: FormData) {
    if (this.isRecurring && this.recurringTaskId) {
      // Update existing recurring task
      const recurringData = {
        frequency: this.frequency,
        startDate: new Date(this.startDate).toISOString(),
        daysInAdvance: this.daysInAdvance,
        ...(this.endDate && { endDate: new Date(this.endDate).toISOString() })
      };

      return this.taskService.updateTask(this.taskId!, formData).pipe(
        switchMap(() => this.taskService.updateRecurringTask(this.recurringTaskId!, recurringData))
      );
    } else if (this.isRecurring && !this.recurringTaskId) {
      // Create new recurring task (add to formData)
      formData.append('isRecurring', 'true');
      formData.append('frequency', this.frequency);
      formData.append('startDate', new Date(this.startDate).toISOString());
      formData.append('daysInAdvance', this.daysInAdvance.toString());
      if (this.endDate) {
        formData.append('endDate', new Date(this.endDate).toISOString());
      }

      return this.taskService.updateTask(this.taskId!, formData);
    } else if (!this.isRecurring && this.recurringTaskId) {
      // Delete recurring task
      return this.taskService.updateTask(this.taskId!, formData).pipe(
        switchMap(() => this.taskService.deleteRecurringTask(this.recurringTaskId!))
      );
    } else {
      // No recurring changes
      return this.taskService.updateTask(this.taskId!, formData);
    }
  }

  // Assignee dropdown methods
  toggleAssigneeDropdown(event: Event) {
    event.stopPropagation();
    this.showAssigneeDropdown = !this.showAssigneeDropdown;
  }

  getFilteredUsers(): User[] {
    if (!this.assignSearchQuery) return this.users;
    const query = this.assignSearchQuery.toLowerCase();
    return this.users.filter(
      (user) =>
        user.username.toLowerCase().includes(query) ||
        user.role.name.toString().toLowerCase().includes(query)
    );
  }

  selectUser(user: User, event: Event) {
    event.stopPropagation();
    this.selectedUser = user;
    this.taskForm.patchValue({ assigneeId: user.id });
    this.showAssigneeDropdown = false;
    this.assignSearchQuery = '';
  }

  removeAssignee() {
    this.selectedUser = null;
    this.taskForm.patchValue({ assigneeId: '' });
  }

  getUserInitials(username: string): string {
    return username
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  // File handling methods
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      Array.from(input.files).forEach((file) => {
        this.uploadedFiles.push({
          fileName: file.name,
          fileSize: file.size,
          file: file,
          isExisting: false,
          fileType: file.type,
        });
      });
    }
  }

  async viewFile(file: UploadedFile) {
    // This is the helper function that forces a download with a specific name.
    const forceDownload = (blob: Blob, filename: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    if (file.file) {
      // Case 1: A new file (File object exists)
      // This is the most direct case. We already have the blob data.
      forceDownload(file.file, file.fileName);

    } else if (file.fileUrl) {
      // Case 2: An existing file from a URL
      try {
        // We fetch the file from the URL.
        // The `mode: 'cors'` is important if the file is on a different domain.
        // Your server MUST be configured to allow CORS requests for this to work.
        const response = await fetch(file.fileUrl, { mode: 'cors' });

        if (!response.ok) {
          // If the fetch fails, fall back to opening in a new tab as a last resort.
          console.error(`Failed to fetch file: ${response.statusText}. Opening in new tab as fallback.`);
          window.open(file.fileUrl, '_blank');
          return;
        }

        // We get the file data as a Blob
        const blob = await response.blob();

        // Now we can use our helper function to force the download with the correct name.
        forceDownload(blob, file.fileName);

      } catch (error) {
        // If an error occurs (e.g., CORS issue), open the URL in a new tab as a fallback.
        // The user will still get the random filename, but at least they can see the file.
        console.error('Error fetching the file for download, likely a CORS issue. See the server configuration note in the code. Opening in a new tab as a fallback.', error);
        window.open(file.fileUrl, '_blank');
      }
    }
  }

  deleteFile(file: UploadedFile, index: number) {
    if (file.isExisting && file.id) {
      this.deletedDocuments.push(file.id);
    }
    this.uploadedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return Math.round(bytes / 1048576) + ' MB';
  }

  // Date handling
  changeDueDate(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const dateInput = document.getElementById('dueDateInput') as HTMLInputElement;
    if (dateInput) {
      // Focus the input first
      dateInput.focus();
      // Then show the picker if supported
      if ('showPicker' in HTMLInputElement.prototype) {
        (dateInput as HTMLInputElement & { showPicker: () => void }).showPicker();
      } else {
        // Fallback: trigger click on the input
        dateInput.click();
      }
    }
  }

  get formattedDueDate(): string {
    const date = this.taskForm.get('dueDate')?.value;
    if (!date) return '';

    const dateObj = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateObj.setHours(0, 0, 0, 0);

    const diffTime = dateObj.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let prefix = '';
    if (diffDays === 0) prefix = 'Due Today';
    else if (diffDays === 1) prefix = 'Due Tomorrow';
    else if (diffDays === -1) prefix = 'Due Yesterday';
    else if (diffDays > 0) prefix = `Due in ${diffDays} days`;
    else prefix = `Overdue by ${Math.abs(diffDays)} days`;

    return prefix + ' | ' + dateObj.toLocaleDateString('en-GB');
  }

  // Comment methods
  loadComments() {
    if (!this.taskId) return;

    this.taskService.getTaskComments(this.taskId).subscribe({
      next: (comments) => {
        this.comments = comments.map((comment) => ({
          ...comment,
          isEditing: false,
          editText: comment.body,
          editFiles: comment.documents ? [...comment.documents] : [],
        }));

        this.comments.reverse();

        // Initialize readonly editors after DOM update
        setTimeout(() => {
          this.comments.forEach(comment => {
            this.initializeReadonlyCommentEditor(comment);
          });
        }, 0);
      },
      error: (error) => console.error('Error loading comments:', error),
    });
  }

  getNewCommentText(): string {
    return this.newCommentQuill?.root.innerText?.trim() || '';
  }

  isCommentEditable(comment: Comment): boolean {
    const commentDate = new Date(comment.createdAt);
    const now = new Date();
    const diffMinutes = (now.getTime() - commentDate.getTime()) / (1000 * 60);
    return diffMinutes <= 15;
  }

  private initializeNewCommentEditor() {
    if (this.newCommentEditorRef) {
      Quill.register({
        'modules/table-better': QuillTableBetter
      }, true);

      this.newCommentQuill = new Quill(this.newCommentEditorRef.nativeElement, {
        theme: 'snow',
        placeholder: 'Add a comment...',
        modules: {
          table: false,
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }], // Add alignment
            ['clean'],
          ],
          'table-better': {
            language: 'en_US',
            menus: ['column', 'row'],
            toolbarTable: false
          },
          keyboard: {
            bindings: QuillTableBetter.keyboardBindings
          },
          clipboard: {
            matchVisual: false
          }
        },
      });

      // Add copy handler
      this.newCommentQuill.root.addEventListener('copy', (e: ClipboardEvent) => {
        if (e.clipboardData) {
          e.preventDefault();
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) return;

          const range = selection.getRangeAt(0);
          const fragment = range.cloneContents();
          const div = document.createElement('div');
          div.appendChild(fragment);

          const html = this.addInlineStylesToTables(div.innerHTML);
          const text = div.textContent || '';

          e.clipboardData.setData('text/html', html);
          e.clipboardData.setData('text/plain', text);
        }
      });
    }
  }

  addComment() {
    const commentText = this.newCommentQuill?.root.innerHTML || '';
    if (!commentText.trim() && this.newCommentFiles.length === 0) return;
    if (!this.taskId) return;

    const formData = new FormData();
    formData.append('body', commentText);

    // Add new files
    this.newCommentFiles.forEach((file) => {
      if (file.file) {
        formData.append('attachments', file.file);
      }
    });

    this.taskService.createComment(this.taskId, formData).subscribe({
      next: (comment) => {
        this.comments.push({
          ...comment,
          isEditing: false,
          editText: comment.body,
          editFiles: comment.documents ? [...comment.documents] : [],
        });
        this.newCommentQuill.setText('');
        this.newCommentFiles = [];

        // Initialize readonly editor for new comment
        setTimeout(() => {
          this.initializeReadonlyCommentEditor(comment);
        }, 0);
      },
      error: (error) => console.error('Error adding comment:', error),
    });
  }

  editComment(comment: Comment) {
    // Cancel any other editing comments
    this.comments.forEach((c) => {
      if (c.id !== comment.id) {
        this.cancelCommentEdit(c);
      }
    });

    comment.isEditing = true;
    comment.editText = comment.body;
    comment.editFiles = comment.documents ? [...comment.documents] : [];
    this.editingCommentId = comment.id;

    // Initialize Quill editor for this comment after DOM update
    setTimeout(() => {
      this.initializeCommentEditor(comment);
    }, 0);
  }

  private initializeCommentEditor(comment: Comment) {
    const editorElement = document.querySelector(`[data-comment-id="${comment.id}"]`) as HTMLElement;
    if (editorElement) {
      Quill.register({
        'modules/table-better': QuillTableBetter
      }, true);

      const quill = new Quill(editorElement, {
        theme: 'snow',
        modules: {
          table: false,
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }], // Add alignment
            ['clean'],
          ],
          'table-better': {
            language: 'en_US',
            menus: ['column', 'row'],
            toolbarTable: false
          },
          keyboard: {
            bindings: QuillTableBetter.keyboardBindings
          },
          clipboard: {
            matchVisual: false
          }
        },
      });

      // Add copy handler
      quill.root.addEventListener('copy', (e: ClipboardEvent) => {
        if (e.clipboardData) {
          e.preventDefault();
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) return;

          const range = selection.getRangeAt(0);
          const fragment = range.cloneContents();
          const div = document.createElement('div');
          div.appendChild(fragment);

          const html = this.addInlineStylesToTables(div.innerHTML);
          const text = div.textContent || '';

          e.clipboardData.setData('text/html', html);
          e.clipboardData.setData('text/plain', text);
        }
      });

      // Set initial content
      quill.root.innerHTML = comment.body || '';
      this.commentEditors.set(comment.id, quill);
    }
  }

  saveCommentEdit(comment: Comment) {
    const quill = this.commentEditors.get(comment.id);
    const editText = quill?.root.innerHTML || '';

    if (!editText.trim() && (!comment.editFiles || comment.editFiles.length === 0)) {
      return;
    }

    const formData = new FormData();
    formData.append('body', editText);

    // Add new files (files that don't have an id)
    const newFiles =
      comment.editFiles?.filter((file) => !file.id && file.file) || [];
    newFiles.forEach((file) => {
      if (file.file) {
        formData.append('attachments', file.file);
      }
    });

    // Track deleted documents
    const originalFileIds = comment.documents?.map((d) => d.id) || [];
    const currentFileIds =
      comment.editFiles?.filter((f) => f.id).map((f) => f.id) || [];
    const deletedFileIds = originalFileIds.filter(
      (id) => !currentFileIds.includes(id)
    );

    if (deletedFileIds.length > 0) {
      formData.append('deletedDocuments', JSON.stringify(deletedFileIds));
    }

    this.taskService.updateComment(comment.id, formData).subscribe({
      next: (updatedComment) => {
        const index = this.comments.findIndex((c) => c.id === comment.id);
        if (index !== -1) {
          this.comments[index] = {
            ...updatedComment,
            isEditing: false,
            editText: updatedComment.body,
            editFiles: updatedComment.documents
              ? [...updatedComment.documents]
              : [],
          };
        }
        this.editingCommentId = null;
      },
      error: (error) => console.error('Error updating comment:', error),
    });
  }

  cancelCommentEdit(comment: Comment) {
    comment.isEditing = false;
    comment.editText = comment.body;
    comment.editFiles = comment.documents ? [...comment.documents] : [];
    this.editingCommentId = null;

    // Cleanup Quill editor
    this.commentEditors.delete(comment.id);
  }

  deleteComment(comment: Comment, index: number) {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    this.taskService.deleteComment(comment.id).subscribe({
      next: () => {
        this.comments.splice(index, 1);
      },
      error: (error) => console.error('Error deleting comment:', error),
    });
  }

  canEditComment(comment: Comment): boolean {
    return comment.author.id === this.currentUser?.id;
  }

  private initializeReadonlyCommentEditor(comment: Comment) {
    const editorElement = document.querySelector(`[data-readonly-comment="${comment.id}"]`) as HTMLElement;
    if (editorElement) {
      const quill = new Quill(editorElement, {
        theme: 'snow',
        readOnly: true,
        modules: {
          toolbar: false,
        },
      });

      // Set content
      quill.root.innerHTML = comment.body || '';

      // Make content selectable and add copy handler
      const editor = quill.root;
      editor.style.userSelect = 'text';
      editor.style.webkitUserSelect = 'text';

      editor.addEventListener('copy', (e: ClipboardEvent) => {
        if (e.clipboardData) {
          e.preventDefault();
          const selection = window.getSelection();
          if (!selection || selection.rangeCount === 0) return;

          const range = selection.getRangeAt(0);
          const fragment = range.cloneContents();
          const div = document.createElement('div');
          div.appendChild(fragment);

          const html = this.addInlineStylesToTables(div.innerHTML);
          const text = div.textContent || '';

          e.clipboardData.setData('text/html', html);
          e.clipboardData.setData('text/plain', text);
        }
      });

      // Store reference
      this.commentEditors.set(`readonly-${comment.id}`, quill);
    }
  }

  formatCommentDate(date: Date): string {
    const now = new Date();
    const commentDate = new Date(date);
    const diffMs = now.getTime() - commentDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Format date + time (e.g., "5 Nov 2025, 3:45 PM")
    return commentDate.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: commentDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatActivityDate(timestamp: string | undefined): string {
    if (!timestamp) return 'Unknown time';

    const activityDate = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - activityDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return activityDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year:
        activityDate.getFullYear() !== now.getFullYear()
          ? 'numeric'
          : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  // Comment file handling
  onCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      Array.from(input.files).forEach((file) => {
        this.newCommentFiles.push({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          file: file,
          isExisting: false,
        });
      });
    }
  }

  onEditFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && this.editingCommentId) {
      const editingComment = this.comments.find(
        (c) => c.id === this.editingCommentId
      );
      if (editingComment) {
        Array.from(input.files).forEach((file) => {
          if (!editingComment.editFiles) {
            editingComment.editFiles = [];
          }
          editingComment.editFiles.push({
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            file: file,
            isExisting: false,
          });
        });
      }
    }
  }

  deleteCommentFile(index: number) {
    this.newCommentFiles.splice(index, 1);
  }

  removeEditFile(comment: Comment, index: number) {
    if (comment.editFiles) {
      const removedFile = comment.editFiles.splice(index, 1)[0];
      // If it's an existing file, track it for deletion
      if (removedFile.id) {
        this.deletedCommentDocuments.push(removedFile.id);
      }
    }
  }

  getDueDateClass(): string {
    if (!this.task) return 'due-later';

    const { dueDate, status } = this.task;

    if (status === 'COMPLETED') return 'completed';
    if (status === 'CANCELLED') return 'cancelled';

    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'due-today'; // Overdue
    if (diffDays === 0) return 'due-today'; // Due today
    if (diffDays <= 3) return 'due-soon'; // Due soon
    return 'due-later'; // Due later
  }

  // Debug method to manually update form from Quill
  updateFormFromQuill() {
    if (this.quill) {
      const content = this.quill.root.innerHTML;
      this.taskForm.patchValue({ description: content });
    }
  }

  formatActivityAction(action: string): string {
    return action.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  formatActivityDetails(details: string): string {
    if (!details) return '';

    // Capitalize first letter
    let formatted = details.charAt(0).toUpperCase() + details.slice(1);

    // Highlight quoted names (text between single quotes)
    formatted = formatted.replace(/'([^']+)'/g, '<span class="highlighted-name">\'$1\'</span>');

    return formatted;
  }

  nudgeAssignee() {
    console.log("ASD")
    this.taskService.nudgeAssignee(this.taskId!).subscribe();
    if (this.task) {
      this.task.lastNudgedAt = new Date().toISOString();
    }
  }

  canNudge() {
    const last = this.task?.lastNudgedAt ? new Date(this.task.lastNudgedAt).getTime() : 0;
    return Date.now() - last > 3600000; // 1 hour
  }
}
